<template>
    <div>
        <AutomationEmailEditorContainer
            title="Opt-in email"
            @close="$emit('close-modal')"
        >
            <template #header>
                <DsToggleSwitch
                    :value="isReady"
                    :label-on="$t('funnel.header.readyToggle.ready')"
                    :label-off="$t('funnel.header.readyToggle.draft')"
                    @input="() => {}"
                />

                <DsButton>
                    {{ $t('funnel.edit.done') }}
                </DsButton>
            </template>

            <template #default>
                <StepsContainer>
                    <component
                        :is="step.component"
                        v-for="step in steps"
                        :key="step.title"
                        :active="isStepActive(step)"
                        :email="email"
                        :saved-subject="subject"
                        :fetch-subject-risks="fetchSubjectRisks"
                        :loading-subject-risk="loadingSubjectRisk"
                        :subject-risk-messages="subjectRiskMessages"
                        :subject-risk-quality="subjectRiskQuality"
                        :subject-risk-sendable="subjectRiskSendable"
                        :html="htmlBody"
                        :saved-to-address="savedToAddress"
                        :content-risk-messages="contentRiskMessages"
                        :content-risk-quality="contentRiskQuality"
                        has-content
                        hide-preview
                        reply-to-enabled
                        show-verified-domains-new-keys-only-enabled
                        wrap-sender-display-name-with-quotes-enabled
                        merge-fields-in-from-address-enabled
                        :environment="appInfo.environment"
                        complete
                        :saved-from="sender.from"
                        :saved-reply-to-address="sender.replyToAddress"
                        :merge-fields="mergeFields"
                        :error="STATUSES && step.status === STATUSES.ERROR"
                        @save="handleStepSave(step, $event)"
                        @error="handleStepError(step, $event)"
                        @cancel="() => handleStepCancel()"
                    >
                        <template #action="{ actionText }">
                            <component
                                :is="isStepNew(step) ? 'DsOutlineButton' : 'DsTextButton'"
                                v-if="!isStepActive(step)"
                                dense
                                :disabled="!!activeStepTitle"
                                @click="activateStep(step.title)"
                            >
                                {{ getActionText({}, actionText) }}
                            </component>
                        </template>
                    </component>
                </StepsContainer>
            </template>
        </AutomationEmailEditorContainer>

        <WizardModal
            v-if="isWizardOpen"
            :app-id="appInfo.appName"
            :user-id="appInfo.userId"
            :environment="appInfo.environment"
            :design="design"
            @close="isWizardOpen = false"
            @design-updated="onDesignUpdated"
        />
    </div>
</template>

<script>
import AutomationEmailEditorContainer from '@/components/unlayer/AutomationEmailEditorContainer.vue';
import StepsContainer from '@/components/unlayer/UnlayerEmailStepsContainer.vue';

import SenderStep from '@/components/broadcast/email/steps/EmailBroadcastSenderStep';
import AudienceStep from '@/components/broadcast/email/steps/EmailAutomationAudienceStep';
import ContentStep from '@/components/broadcast/email/steps/EmailBroadcastContentStep';
import SubjectStep from '@/components/broadcast/email/steps/EmailBroadcastSubjectStep.vue';

import WizardModal from '@/components/unlayer/ConfirmationEmailWizardModal.vue';

import useContentRiskActions from '@/components/unlayer/composables/useContentRiskActions/useContentRiskActions';
import useSubjectRiskActions from '@/components/unlayer/composables/useContentRiskActions/useSubjectRiskActions';
import {
    getMergeFields,
} from '@/api/campaigns/campaignUnlayerEmail';
import {
    DEFAULT_FROM_VALUE,
} from '@/components/broadcast/constants/emailBroadcast.constants';
import sentry from '@/analytics/sentry';


import {
    STATUSES,
    STEPS,
} from '@/components/unlayer/constants/editor.constants';
import {
    createNewUnlayerEmail,
} from '@/components/unlayer/unlayerEmail.util';

export default {
    name: 'ConfirmationEmailEditorPage',

    components: {
        AutomationEmailEditorContainer,
        StepsContainer,
        SenderStep,
        SubjectStep,
        AudienceStep,
        WizardModal,
    },

    props: {
        appInfo: {
            type: Object,
            default: () => ({}),
        },
        funnelId: {
            type: [String, Number],
            required: true,
        },
    },

    data() {
        return {
            isReady: false,
            STATUSES,
            STEPS,
            steps: [{
                component: SenderStep,
                status: STATUSES.COMPLETE,
                title: STEPS.SENDER,
            }, {
                component: SubjectStep,
                status: STATUSES.NEW,
                title: STEPS.SUBJECT,
            }, {
                component: AudienceStep,
                status: STATUSES.COMPLETE,
                title: STEPS.AUDIENCE,
            }, {
                component: ContentStep,
                status: STATUSES.COMPLETE,
                title: STEPS.CONTENT,
            }],
            errors: [],

            // I think creating the email object was a mistake
            // or atleast we shouldn't be passing this object
            // to the step components. Feels cleaner to pass
            // props that contain strings/numbers etc instead of objects.
            // Better to refactor other steps so that they take in what they
            // need like say subject, preview as individual props instead of
            // taking in an email object that contains a bunch of things the
            // component doesn't care about. This change has been made for
            // Audience step as of this commit.
            email: createNewUnlayerEmail(),
            subject: '',
            htmlBody: '',
            savedToAddress: '',
            design: { // temp design
                counters: {},
                body: {
                    rows: [],
                },
                schemaVersion: 21,
            },

            activeStepTitle: '',
            isWizardOpen: false,
            sender: {
                from: DEFAULT_FROM_VALUE,
                replyToAddress: null,
            },
            mergeFields: [],
        };
    },

    mounted() {
        this.fetchMergeFields();
    },

    setup() {
        const result = useContentRiskActions();
        const subjectRisk = useSubjectRiskActions();

        return {
            contentRiskQuality: result.quality,
            contentRiskMessages: result.messages,
            fetchSubjectRisks: subjectRisk.fetch,
            loadingSubjectRisk: subjectRisk.isLoading,
            subjectRiskSendable: subjectRisk.sendable,
            subjectRiskQuality: subjectRisk.quality,
            subjectRiskMessages: subjectRisk.messages,
        };
    },

    methods: {
        isStepActive(step) {
            return step.title === this.activeStepTitle;
        },

        isStepNew(step) {
            return step.status === STATUSES.INCOMPLETE;
        },

        activateStep(stepTitle) {
            if (stepTitle === STEPS.CONTENT) {
                this.isWizardOpen = true;
            } else if (this.steps.map((s) => s.title).includes(stepTitle)) {
                this.activeStepTitle = stepTitle;
            }
        },

        getActionText({ title, status } = {}, text) {
            if (status !== STATUSES.NEW) {
                return this.$t('global.edit');
            }

            if (title === STEPS.CONTENT) {
                return this.$t('email.broadcast.steps.content.actionText');
            }

            if (title === STEPS.SUBJECT) {
                return this.$t('email.broadcast.steps.subject.actionText');
            }

            return text;
        },

        onDesignUpdated({ design, html }) {
            this.design = design;
            this.htmlBody = html;
        },

        handleStepSave(step, payload) {
            switch (step.title) {
            case STEPS.SUBJECT:
                this.subject = payload.subject;
                break;
            case STEPS.AUDIENCE:
                this.savedToAddress = payload.toAddress;
                break;
            case STEPS.SENDER:
                this.sender.from = payload.from;
                this.sender.replyToAddress = payload.replyToAddress;
                break;
            default:
                return;
            }

            this.setStepStatus(step.title, STATUSES.COMPLETE);
            this.removeAllErrorsForCategory(step.title);

            this.activeStepTitle = null;
        },

        handleStepError(step, error) {
            console.error(`Error in ${step}:`, error);
            this.showErrors = true;

            this.setStepStatus(step.title, STATUSES.ERROR);

            const errorMessage = (() => {
                switch (error) {
                // TODO add switch cases for future steps
                default:
                    return error;
                }
            })();

            this.errorAdd({
                message: errorMessage,
                category: step.title,
            });

            if (this.errors.length === 1) {
                this.activateStep(step.title);
            }
        },

        handleStepCancel() {
            this.activeStepTitle = null;
        },

        setStepStatus(stepTitle, status) {
            const step = this.steps.find((s) => s.title === stepTitle);

            if (step) step.status = status;
        },

        errorAdd({ message, category }) {
            const errorExists = this.errors.some(({ message: m }) => message === m);

            if (!errorExists) {
                this.errors.push({
                    message,
                    category,
                });
            }
        },

        removeAllErrorsForCategory(cat) {
            if (this.errors.filter(({ category }) => category === cat).length === 0) return;

            this.errors = this.errors.filter(({ category }) => cat !== category);
        },

        async fetchMergeFields() {
            try {
                this.mergeFields = await getMergeFields(this.funnelId);
            } catch (e) {
                sentry.captureException(e);
            }
        },
    },
};
</script>
